export interface CopyMediaSource {
  url?: string;
  file?: File;
  filename: string;
  contentType?: string;
  /** Bereits angezeigtes Bild – Fallback wenn kein fetch möglich. */
  imageElement?: HTMLImageElement | null;
}

type ClipboardPresentationStyle = "unspecified" | "inline" | "attachment";

function assertClipboardWrite(): void {
  if (!navigator.clipboard?.write) {
    throw new Error("Zwischenablage ist in diesem Browser nicht verfügbar.");
  }
}

function ensureDocumentFocus(): void {
  try {
    window.focus();
  } catch {
    // ignore
  }
}

function ensureImageLoaded(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("Bild konnte nicht geladen werden."));
    };
    const cleanup = () => {
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
    };
    img.addEventListener("load", onLoad);
    img.addEventListener("error", onError);
  });
}

function imageElementToPngBlob(img: HTMLImageElement): Promise<Blob> {
  return ensureImageLoaded(img).then(
    () =>
      new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Bild konnte nicht konvertiert werden."));
          return;
        }
        try {
          ctx.drawImage(img, 0, 0);
        } catch {
          reject(new Error("Bild konnte nicht gelesen werden."));
          return;
        }
        canvas.toBlob(
          (b) =>
            b
              ? resolve(b)
              : reject(new Error("Bild konnte nicht konvertiert werden.")),
          "image/png",
        );
      }),
  );
}

function resolveBlob(source: CopyMediaSource): Promise<Blob> {
  if (source.file) {
    return Promise.resolve(source.file);
  }
  if (!source.url) {
    return Promise.reject(new Error("Keine Quelle zum Kopieren."));
  }
  return fetch(source.url, { credentials: "include", cache: "no-store" }).then((resp) => {
    if (!resp.ok) {
      throw new Error("Datei konnte nicht geladen werden.");
    }
    return resp.blob();
  });
}

function blobToFile(blob: Blob, filename: string, contentType?: string): File {
  const type = contentType || blob.type || "application/octet-stream";
  const payload = blob.type ? blob : new Blob([blob], { type });
  return new File([payload], filename, { type });
}

/** Browser akzeptieren beim Clipboard-Schreiben i. d. R. nur image/png. */
function ensurePngFilename(filename: string): string {
  if (filename.toLowerCase().endsWith(".png")) return filename;
  const base = filename.replace(/\.[^.]+$/, "") || "bild";
  return `${base}.png`;
}

async function blobToPngFile(blob: Blob, filename: string): Promise<File> {
  if (blob.type === "image/png") {
    return blobToFile(blob, ensurePngFilename(filename), "image/png");
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(blob);
  } catch {
    throw new Error("Bild konnte nicht gelesen werden.");
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Bild konnte nicht konvertiert werden.");
    }
    ctx.drawImage(bitmap, 0, 0);
    const pngBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) =>
          b
            ? resolve(b)
            : reject(new Error("Bild konnte nicht konvertiert werden.")),
        "image/png",
      );
    });
    return blobToFile(pngBlob, ensurePngFilename(filename), "image/png");
  } finally {
    bitmap.close();
  }
}

/** Bild als File auflösen (PNG für Clipboard; Originalformat wenn möglich für Öffnen/Druck). */
export function resolveImageFile(source: CopyMediaSource): Promise<File> {
  const filename = source.filename || "bild.png";

  if (source.file) {
    return blobToPngFile(source.file, filename);
  }

  if (source.url) {
    return resolveBlob(source).then((blob) => {
      const type = source.contentType || blob.type || "image/jpeg";
      if (!type.startsWith("image/")) {
        throw new Error("Kein Bild.");
      }
      return blobToPngFile(blob, filename);
    });
  }

  if (source.imageElement) {
    return imageElementToPngBlob(source.imageElement).then((blob) =>
      blobToFile(blob, ensurePngFilename(filename), "image/png"),
    );
  }

  return Promise.reject(new Error("Keine Quelle zum Kopieren."));
}

function resolveFile(source: CopyMediaSource): Promise<File> {
  if (source.file) {
    return Promise.resolve(source.file);
  }
  return resolveBlob(source).then((blob) =>
    blobToFile(blob, source.filename, source.contentType || blob.type),
  );
}

/**
 * Schreibt sofort in die Zwischenablage (während der User-Geste) und lädt
 * die Datei asynchron nach.
 */
async function writeClipboardFile(
  type: string,
  filePromise: Promise<File>,
  presentationStyle: ClipboardPresentationStyle = "attachment",
): Promise<void> {
  assertClipboardWrite();
  ensureDocumentFocus();
  await navigator.clipboard.write([
    new ClipboardItem(
      {
        [type]: filePromise,
      },
      { presentationStyle },
    ),
  ]);
}

/**
 * Bildbytes für Öffnen/Druck – behält Originalformat wenn möglich
 * (im Gegensatz zu resolveImageFile, das fürs Clipboard nach PNG konvertiert).
 */
export async function resolveImageBlob(source: CopyMediaSource): Promise<File> {
  const filename = source.filename || "bild.jpg";

  if (source.file && source.file.type.startsWith("image/")) {
    return source.file;
  }

  if (source.url) {
    const blob = await resolveBlob(source);
    const type = source.contentType || blob.type || "image/jpeg";
    if (!type.startsWith("image/") && !blob.type.startsWith("image/")) {
      throw new Error("Kein Bild.");
    }
    const finalType = type.startsWith("image/") ? type : blob.type || "image/jpeg";
    return blobToFile(blob, filename, finalType);
  }

  if (source.imageElement) {
    const blob = await imageElementToPngBlob(source.imageElement);
    return blobToFile(blob, ensurePngFilename(filename), "image/png");
  }

  if (source.file) {
    return blobToPngFile(source.file, filename);
  }

  throw new Error("Keine Quelle.");
}

/** Bild als Dateianhang in die Zwischenablage legen (z. B. für Outlook). */
export async function copyImageToClipboard(source: CopyMediaSource): Promise<void> {
  // Chromium/Edge unterstützen beim Clipboard-Schreiben nur image/png, nicht JPEG/WebP.
  await writeClipboardFile("image/png", resolveImageFile(source), "attachment");
}

/** Datei/Dokument als Anhang in die Zwischenablage legen (PDF, Bilddatei, …). */
export async function copyFileToClipboard(source: CopyMediaSource): Promise<void> {
  const fallbackType = source.contentType || source.file?.type || "application/octet-stream";
  await writeClipboardFile(fallbackType, resolveFile(source), "attachment");
}

export function isImageContentType(contentType?: string | null): boolean {
  return (contentType || "").startsWith("image/");
}

/** Hilfsfunktion: img-Element aus dem Rechtsklick-Ziel ermitteln. */
export function imageElementFromContextEvent(
  target: EventTarget | null,
  currentTarget: EventTarget | null,
): HTMLImageElement | null {
  if (target instanceof HTMLImageElement) return target;
  if (currentTarget instanceof HTMLElement) {
    const img = currentTarget.querySelector("img");
    if (img instanceof HTMLImageElement) return img;
  }
  return null;
}
